import React, { useContext, useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { PortalPageShell, Toast } from "src/components";
import { UserContext } from "src/scenes/App/UserContext";
import { UserKeys } from "src/constants/userDetails";
import { MyQuotesHeader, MyQuotesTable, PageLoadingContainer } from "./components";
import ManageQuotesModal from "./components/Modal/ManageQuotesModal";
import ChangeNameDialog from "./components/Modal/ChangeNameDialog";
import { getQuotes, getCompanyInformation, getSettings } from "./components/api/fetchQuotes";
import QuotesForm from "./components/forms/QuotesForm";

export const DefaultMyQuotesFeatureName = "Quotes";

const MyQuotes = ({ user }) => {

    const { appFns } = useContext(UserContext);

    const [featureName, setFeatureName] = useState(user[UserKeys.CUSTOM_NAME_MY_QUOTES] || DefaultMyQuotesFeatureName);
    const [showManageQuotesModal, setShowManageQuotesModal] = useState(false);
    const [showAddQuoteForm, setShowAddQuoteForm] = useState(false);
    const [showChangeNameDialog, setShowChangeNameDialog] = useState(false);
    const [quotes, setQuotes] = useState([]);
    const [settingsPreview, setSettingsPreview] = useState([]);
    const [fetching, setFetching] = useState(false);
    const [companyUuid, setCompanyUuid] = useState(null);
    const [selectedQuote, setSelectedQuote] = useState(null);

    const toggleManageQuotesModal = () => setShowManageQuotesModal(prev => !prev);
    const toggleAddQuoteForm = (quote = null) => {
        const fullQuote = quote?.uuid
            ? quotes.find(q => q.uuid === quote.uuid) ?? null
            : null;

        setSelectedQuote(fullQuote);
        setShowAddQuoteForm(prev => !prev);
    };

    const propagateFeatureName = newName => {
        setFeatureName(newName);
        appFns.updateUserContextProperties({
            [UserKeys.CUSTOM_NAME_MY_QUOTES]: newName
        });
    };

    useEffect(() => {
        const loadQuotes = async () => { 
            setFetching(true);
            try {
                const company = await getCompanyInformation(user);
                setCompanyUuid(company.uuid);

                const { quotes: fetchedQuotes } = await getQuotes(user, company.uuid);
                const filteredQuotes = fetchedQuotes.filter((item) => !item.deleted_at);
                if (quotes)
                    setQuotes(filteredQuotes);

                const settings = await getSettings(user);
                setSettingsPreview(settings);
            } catch {
                Toast.error("Error fetching quotes");
            } finally {
                setFetching(false);
            }
        };

        loadQuotes();
    }, [user]);

    if (showAddQuoteForm) {
        return (
            <QuotesForm 
                companyUuid={companyUuid}
                user={user}
                setQuotes={setQuotes}
                settingsPreview={settingsPreview}
                quote={selectedQuote || null} 
                manageQuotes={{ createQuotes: toggleAddQuoteForm }}
            />
        );   
    }

    return (
        <PortalPageShell>
            <MyQuotesHeader 
                showManageQuotesModal={{ showManageQuotesModal, toggleCreateQuoteForm: toggleManageQuotesModal }}
                manageQuotes={{ showAddQuoteForm, createQuotes: toggleAddQuoteForm }}
                quotes={quotes}
                featureName={featureName}
                setShowChangeNameDialog={setShowChangeNameDialog}
            />
            {/* Table */}
            <PageLoadingContainer isFetching={fetching}>
                <MyQuotesTable
                    data={quotes.map((q) => ({
                        uuid: q.uuid,
                        quoteImage: q.imageUrl,
                        quoteText: q.quoteText,
                        articleImage: q.article?.imageUrl,
                        articleTitle: q.article?.title,
                        articleDescription: q.article?.articleHtml,
                    }))}
                    manageQuotes={{ showAddQuoteForm, createQuotes: toggleAddQuoteForm }}
                    setQuotesFromParent={setQuotes}
                />
            </PageLoadingContainer>
            {/* Modal */}
            {showManageQuotesModal && (
                <ManageQuotesModal
                    show={showManageQuotesModal}
                    onClose={toggleManageQuotesModal}
                    user={user}
                    companyUuid={companyUuid}
                />
            )}
            {showChangeNameDialog && (
                <ChangeNameDialog
                    hideShowNameChangeDialog={() => setShowChangeNameDialog(false)}
                    featureName={featureName}
                    setFeatureName={propagateFeatureName}
                />
            )}
        </PortalPageShell>
    );
};

export default withRouter(MyQuotes);
